import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { gsap, Power2 } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import vertex from "./shaders/vertex.glsl";
import fragment from "./shaders/fragment.glsl";

gsap.registerPlugin(ScrollTrigger, SplitText);

// DOM 获取
const container = document.getElementById("slider");
const images = JSON.parse(container.getAttribute("data-images"));
const textGroups = document.querySelectorAll(".text-group");

const textureLoader = new THREE.TextureLoader();
let textures = [];
let current = 0;
let isRunning = false;

loadTextures(images).then((loadedTextures) => {
  textures = loadedTextures;

  const canvas = document.querySelector("canvas.webgl");
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    75,
    container.offsetWidth / container.offsetHeight,
    0.1,
    100
  );
  camera.position.z = 3;
  scene.add(camera);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.enableZoom = false;

  const geometry = new THREE.PlaneGeometry(1, 1, 2, 2);

  const material = new THREE.ShaderMaterial({
    extensions: {
      derivatives: "#extension GL_OES_standard_derivatives : enable",
    },
    side: THREE.DoubleSide,
    uniforms: {
      time: { value: 0 },
      progress: { value: 0 },
      border: { value: 0 },
      intensity: { value: 0 },
      scaleX: { value: 40 },
      scaleY: { value: 40 },
      transition: { value: 40 },
      swipe: { value: 0 },
      width: { value: 0.35 },
      radius: { value: 0.9 },
      texture1: { value: textures[0] },
      texture2: { value: textures[1] },
      displacement: { value: textureLoader.load("image/disp1.jpg") },
      resolution: { value: new THREE.Vector4() },
    },
    fragmentShader: fragment,
    vertexShader: vertex,
  });

  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  const renderer = new THREE.WebGLRenderer({ canvas: canvas });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  function resize() {
    const width = container.offsetWidth;
    const height = container.offsetHeight;

    renderer.setSize(width, height);
    camera.aspect = width / height;

    const imageAspect = textures[0].image.height / textures[0].image.width;
    let a1, a2;
    if (height / width > imageAspect) {
      a1 = (width / height) * imageAspect;
      a2 = 1;
    } else {
      a1 = 1;
      a2 = height / width / imageAspect;
    }

    material.uniforms.resolution.value.set(width, height, a1, a2);
    const dist = camera.position.z;
    camera.fov = 2 * (180 / Math.PI) * Math.atan(1 / (2 * dist));
    mesh.scale.x = camera.aspect;
    mesh.scale.y = 1;
    camera.updateProjectionMatrix();
  }

  resize();
  window.addEventListener("resize", resize);

  /**
   * Animate
   */
  const clock = new THREE.Clock();
  const tick = () => {
    const elapsedTime = clock.getElapsedTime();
    material.uniforms.time.value = elapsedTime;
    controls.update();
    renderer.render(scene, camera);
    window.requestAnimationFrame(tick);
  };
  tick();

  /**
   * GSAP ScrollTrigger 控制切换
   */
  //3
  const sections = images.length;
  //window height 1530
  const sectionHeight = window.innerHeight;

  document.body.style.height = `${sections * sectionHeight}px`;

  for (let i = 0; i < sections; i++) {
    ScrollTrigger.create({
      // #slider
      trigger: container,
      // start: () => `${i * sectionHeight}px top`,
      // end: () => `${(i + 1) * sectionHeight}px top`,

      start: () => `${i * sectionHeight - sectionHeight * 0.5}px top`,
      end: () => `${(i + 1) * sectionHeight - sectionHeight * 0.5}px top`,

      scrub: true,
      // markers: true,
      onEnter: () => goToSection(i),
      onEnterBack: () => goToSection(i),
    });
  }

  //   如果你想象这是一套「图片切换系统」，那 isRunning 就像一个「正在切换」的标志，current 就是当前显示的图, index 是即将切换到的图。

  function goToSection(index) {
    if (isRunning || index === current) return;
    isRunning = true;

    const nextTexture = textures[index];
    material.uniforms.texture2.value = nextTexture;
    let hasAnimatedText = false; // 标志位

    gsap.to(material.uniforms.progress, {
      value: 1,
      duration: 1.5,
      ease: "power2.out",
      onUpdate: () => {
        const currentProgress = material.uniforms.progress.value;

        // 当 progress 大于 0.3 且尚未触发动画时，调用 animateTitle
        if (!hasAnimatedText && currentProgress >= 0.6) {
          animateTitle(index);
          hasAnimatedText = true;
        }
      },
      // onComplete 是动画完成之后的回调
      // 它在做几件事：

      // 把 texture2（动画过渡到的图）赋值给 texture1，下次过渡就从它开始

      // 把 progress 重置为 0

      // 更新 current = index，告诉系统“现在我们在第 index 个 section”

      // 把 isRunning 设置为 false，允许下一次触发 goToSection

      onComplete: () => {
        material.uniforms.texture1.value = nextTexture;
        material.uniforms.progress.value = 0;
        current = index;
        isRunning = false;
      },
    });
  }

  /**
   * Animate H1 titles
   */
  //titles are all h1 elements inside .title
  // [...titles] 这个是 ES6 的展开运算符，把类数组的 NodeList 转成真正的数组。
  gsap.set(textGroups, { opacity: 1 });
  const splitTextGroups = [...textGroups].map((group) => {
    const headline = group.querySelector(".headline");
    const subtitle = group.querySelector(".subtitle");

    return {
      group,
      headline: new SplitText(headline, { type: "chars" }),
      subtitle: new SplitText(subtitle, { type: "chars" }),
    };
  });

  function animateTitle(index) {
    splitTextGroups.forEach(({ group, headline, subtitle }, i) => {
      // 清除旧动画
      gsap.killTweensOf([headline.chars, subtitle.chars, group]);

      if (i === index) {
        // 先重置字符位置
        gsap.set([headline.chars, subtitle.chars], {
          y: "-100%",
          opacity: 0,
        });

        // 整体 clip-path reveal
        gsap.to(group, {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 0.6,
          ease: "power2.out",
        });

        // 然后字符进入动画
        gsap.to(headline.chars, {
          y: "0%",
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          stagger: 0.05,
          delay: 0.1,
        });

        gsap.to(subtitle.chars, {
          y: "0%",
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          stagger: 0.02,
          delay: 0.4,
        });
      } else {
        // 先隐藏字符（避免 clip 动画中还显示文字）
        gsap.to([headline.chars, subtitle.chars], {
          opacity: 0,
          y: "100%",
          duration: 0.3,
          ease: "power2.in",
        });

        // 隐藏整个 group（clip 收起）
        gsap.to(group, {
          clipPath: "inset(100% 0% 0% 0%)",
          duration: 0.5,
          ease: "power2.inOut",
        });
      }
    });
  }

  // 初始化第一个 title
  animateTitle(0);
});

/**
 * 加载所有纹理
 */
function loadTextures(urls) {
  return Promise.all(
    urls.map((url) => {
      return new Promise((resolve) => {
        textureLoader.load(url, resolve);
      });
    })
  );
}

window.onbeforeunload = () => {
  window.scrollTo(0, 0);
};
